import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useRealChat } from '../contexts/RealChatContext';
import './ChatIndividual.css';
import perfilImg from '../assets/perfil.png';

const ChatMenu: React.FC<{ onViewProfile: () => void }> = ({ onViewProfile }) => {
  const { activeConversation, setShowChat, markAsUnread, deleteConversation, toggleMute, isMuted } = useRealChat();
  const [open, setOpen] = useState(false);

  if (!activeConversation) return null;

  const muted = isMuted(activeConversation.id);

  return (
    <div className="chat-actions" style={{ position: 'relative' }}>
      <button
        className="action-btn"
        aria-label="Menú"
        onClick={() => setOpen(o => !o)}
      >
        ⋮
      </button>
      {open && (
        <div className="chat-menu" onClick={(e) => e.stopPropagation()}>
          <button className="chat-menu-item" onClick={() => { toggleMute(activeConversation.id); setOpen(false); }}>
            <span className="mi">🔕</span>
            {muted ? 'Activar notificaciones' : 'Silenciar notificaciones'}
          </button>
          <button className="chat-menu-item" onClick={() => { markAsUnread(activeConversation.id); setOpen(false); }}>
            <span className="mi">📩</span>
            Marcar como no leída
          </button>
          <button className="chat-menu-item" onClick={() => { onViewProfile(); setOpen(false); }}>
            <span className="mi">👤</span>
            Ver perfil
          </button>
          <button className="chat-menu-item danger" onClick={async () => { await deleteConversation(activeConversation.id); setOpen(false); setShowChat(false); }}>
            <span className="mi">🗑️</span>
            Borrar conversación
          </button>
        </div>
      )}
    </div>
  );
};

const ChatIndividual: React.FC = () => {
  const { 
    activeConversation, 
    messages, 
    showChat, 
    setShowChat, 
    setShowChatList,
    sendMessage,
    currentUser,
    markAsRead
  } = useRealChat();
  
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showResenas, setShowResenas] = useState(false);
  const [resenasSeleccionadas, setResenasSeleccionadas] = useState<any[]>([]);
  const [showPhoneAlert, setShowPhoneAlert] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Función para detectar números de teléfono en el texto
  const containsPhoneNumber = (text: string): boolean => {
    // Normalizar el texto: remover espacios, guiones, puntos, paréntesis y otros separadores
    // Esto ayuda a detectar números incluso cuando están separados artificialmente
    const normalizedText = text.replace(/[\s\-_.()]/g, '');
    
    // También crear una versión que solo remueve espacios múltiples para detectar patrones más amplios
    const textWithSingleSpaces = text.replace(/\s+/g, ' ');
    
    // Patrones para detectar números de teléfono argentinos e internacionales
    const phonePatterns = [
      // Números argentinos con código de área (ej: +54 11 1234-5678, 011 1234-5678)
      /(\+?54\s?)?(0?11|0?221|0?261|0?351|0?381|0?342|0?343|0?344|0?345|0?346|0?347|0?348|0?349|0?370|0?371|0?372|0?373|0?374|0?375|0?376|0?377|0?378|0?379|0?380|0?382|0?383|0?384|0?385|0?386|0?387|0?388|0?389|0?390|0?391|0?392|0?393|0?394|0?395|0?396|0?397|0?398|0?399)[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d/,
      // Números con formato internacional con separadores variables
      /\+?\d{1,4}[\s\-._()]+\d[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d/,
      // Números con guiones, espacios, puntos o cualquier separador (ej: 1-2-3-4-5-6-7-8-9-0)
      /\d[\s\-._()]+\d[\s\-._()]+\d[\s\-._()]+\d[\s\-._()]+\d[\s\-._()]+\d[\s\-._()]+\d[\s\-._()]+\d[\s\-._()]+\d/,
      // Números de 10 dígitos consecutivos en texto normalizado
      /\d{10,}/,
      // Números argentinos sin código de área con separadores
      /15[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d[\s\-._()]*\d/,
    ];

    // Detectar números escritos en palabras (números en letras)
    const numberWords = [
      'cero', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve',
      'diez', 'once', 'doce', 'trece', 'catorce', 'quince'
    ];
    
    // Palabras clave relacionadas con números de teléfono
    const phoneKeywords = [
      /tel[eé]fono/i,
      /n[uú]mero/i,
      /celular/i,
      /whatsapp/i,
      /wsp/i,
      /llamame/i,
      /llamar/i,
      /contacto/i,
      /comunicate/i,
      /llam[aá]/i
    ];

    // Detectar si hay palabras clave de teléfono seguidas de números o palabras numéricas
    const hasPhoneKeyword = phoneKeywords.some(keyword => keyword.test(text));
    const hasNumberWord = numberWords.some(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'i');
      return regex.test(text);
    });
    
    // Si hay palabra clave + palabra numérica, probablemente es un intento de compartir número
    if (hasPhoneKeyword && hasNumberWord) {
      // Contar cuántas palabras numéricas hay
      const numberWordCount = numberWords.filter(word => {
        const regex = new RegExp(`\\b${word}\\b`, 'i');
        return regex.test(text);
      }).length;
      
      // Si hay 4 o más palabras numéricas con una palabra clave de teléfono, es sospechoso
      if (numberWordCount >= 4) {
        return true;
      }
    }

    // Verificar patrones en el texto original
    if (phonePatterns.some(pattern => pattern.test(text))) {
      return true;
    }

    // Verificar patrones en el texto normalizado (sin espacios ni separadores)
    if (phonePatterns.some(pattern => pattern.test(normalizedText))) {
      return true;
    }

    // Verificar patrones en el texto con espacios únicos
    if (phonePatterns.some(pattern => pattern.test(textWithSingleSpaces))) {
      return true;
    }

    // Detectar secuencias de dígitos separados por cualquier carácter (incluyendo letras)
    // Ejemplo: "1.2.3.4.5.6.7.8.9.0" o "1a2b3c4d5e6f7g8h9i0" o "011a1234b5678"
    const digitsOnly = text.replace(/[^\d]/g, '');
    if (digitsOnly.length >= 8) {
      // Crear un patrón que busque dígitos separados por cualquier carácter no dígito
      // Esto detecta casos como: 011a1234b5678, 011x1234y5678, etc.
      const digitSequencePattern = /\d+[a-zA-Z]+\d+[a-zA-Z]+\d+/;
      if (digitSequencePattern.test(text)) {
        return true;
      }
      
      // Detectar si hay dígitos seguidos de letras seguidas de más dígitos
      // Patrón más específico para números de teléfono con letras como separadores
      const phoneWithLetterSeparators = /(\+?54\s?)?(0?11|0?221|0?261|0?351|0?381|0?342|0?343|0?344|0?345|0?346|0?347|0?348|0?349|0?370|0?371|0?372|0?373|0?374|0?375|0?376|0?377|0?378|0?379|0?380|0?382|0?383|0?384|0?385|0?386|0?387|0?388|0?389|0?390|0?391|0?392|0?393|0?394|0?395|0?396|0?397|0?398|0?399)[a-zA-Z]*\d+[a-zA-Z]*\d+[a-zA-Z]*\d+/;
      if (phoneWithLetterSeparators.test(text)) {
        return true;
      }
      
      // Detectar números argentinos con letras como separadores
      const argentinePhoneWithLetters = /15[a-zA-Z]*\d+[a-zA-Z]*\d+[a-zA-Z]*\d+[a-zA-Z]*\d+/;
      if (argentinePhoneWithLetters.test(text)) {
        return true;
      }
      
      // Contar cuántos caracteres no dígitos hay entre dígitos
      const separators = text.replace(/[^\d\s\-._()]/g, '').length - digitsOnly.length;
      
      // Si hay muchos separadores entre los dígitos, es probablemente un intento de ocultar un número
      if (separators >= digitsOnly.length * 0.3) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (activeConversation && showChat) {
      markAsRead(activeConversation.id);
    }
  }, [activeConversation, showChat, markAsRead]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;

    const messageContent = newMessage.trim();
    
    // Verificar si el mensaje contiene números de teléfono
    if (containsPhoneNumber(messageContent)) {
      setShowPhoneAlert(true);
      return;
    }

    setNewMessage('');
    setIsTyping(true);

    try {
      await sendMessage(activeConversation.id, messageContent);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatNameWithInitials = (name: string, apellido?: string) => {
    // Si viene apellido separado, usarlo directamente
    if (apellido) {
      const firstName = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
      const lastName = apellido.charAt(0).toUpperCase() + apellido.slice(1).toLowerCase();
      return `${firstName} ${lastName}`;
    }

    // Fallback: intentar dividir el nombre completo
    const parts = name.split(' ');
    if (parts.length >= 2) {
      const firstName = parts[0];
      const lastName = parts[parts.length - 1];
      return `${firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()} ${lastName.charAt(0).toUpperCase() + lastName.slice(1).toLowerCase()}`;
    }
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  const getOtherParticipant = () => {
    if (!activeConversation || !currentUser) return null;
    
    if (activeConversation.participant1Id === currentUser.id) {
      return {
        id: activeConversation.participant2Id,
        name: activeConversation.participant2Name,
        apellido: activeConversation.participant2Apellido || '',
        type: activeConversation.participant2Type
      };
    } else {
      return {
        id: activeConversation.participant1Id,
        name: activeConversation.participant1Name,
        apellido: activeConversation.participant1Apellido || '',
        type: activeConversation.participant1Type
      };
    }
  };

  const renderMessage = (message: any) => {
    const isOwn = message.senderId === currentUser?.id;

    return (
      <div key={message.id} className={`message ${isOwn ? 'own' : 'other'}`}>
        <div className="message-content">
          <div className="message-bubble">
            <div className="message-text">
              {message.type === 'location' && '📍 '}
              {message.type === 'image' && '🖼️ '}
              {message.type === 'file' && '📎 '}
              {message.content}
            </div>
            
            <div className="message-time">
              {formatTime(message.timestamp)}
              {isOwn && message.read && <span className="read-indicator">✓✓</span>}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!showChat || !activeConversation) return null;

  const otherParticipant = getOtherParticipant();

  return createPortal(
    <div className="chat-overlay" onClick={() => setShowChat(false)}>
      <div className="chat-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="chat-header">
          <button 
            className="back-btn"
            onClick={() => { setShowChat(false); setShowChatList(true); }}
          >
            ←
          </button>
          <div className="chat-participant">
            <div className="participant-avatar">
              <div className="avatar-placeholder">
                {otherParticipant?.name.charAt(0).toUpperCase()}
              </div>
            </div>
            <div className="participant-info">
              <div className="participant-name">
                {otherParticipant?.name ? formatNameWithInitials(otherParticipant.name, otherParticipant.apellido) : ''}
              </div>
              <div className="participant-status">
                {otherParticipant?.type === 'soy_iyire' ? 'Iyire' : 'Cliente'}
              </div>
            </div>
          </div>
          <ChatMenu onViewProfile={() => setShowProfile(true)} />
        </div>

        {/* Job Info */}
        {activeConversation.jobTitle && (
          <div className="chat-job-info">
            <span className="job-badge">{activeConversation.jobTitle}</span>
          </div>
        )}

        {/* Messages */}
        <div className="messages-container">
          {messages.length === 0 ? (
            <div className="empty-messages">
              <div className="empty-icon">💬</div>
              <p>No hay mensajes aún</p>
              <span>¡Envía el primer mensaje!</span>
            </div>
          ) : (
            messages.map(renderMessage)
          )}
          
          {isTyping && (
            <div className="typing-indicator">
              <div className="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <div className="message-input-container">
          <div className="message-input-wrapper">
            <button className="attach-btn" type="button">📎</button>
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe tu mensaje..."
              className="message-input"
              rows={1}
              disabled={isTyping}
            />
            <button 
              type="submit" 
              className="send-btn"
              disabled={!newMessage.trim() || isTyping}
              onClick={handleSendMessage}
            >
              {isTyping ? '⏳' : ''}
            </button>
          </div>
        </div>
        {/* Perfil modal - idéntico al de postulaciones */}
        {showProfile && (
          <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setShowProfile(false)}>
            <div style={{background:'#fff',borderRadius:16,padding:32,minWidth:500,maxWidth:600,boxShadow:'0 8px 32px rgba(0,0,0,0.3)',position:'relative',maxHeight:'90vh',overflowY:'auto'}} onClick={e=>e.stopPropagation()}>
              <button onClick={()=>setShowProfile(false)} style={{position:'absolute',top:16,right:16,background:'none',border:'none',fontSize:24,cursor:'pointer',color:'#888'}}>×</button>
              {/* Header del perfil */}
              <div style={{display:'flex',gap:20,marginBottom:24,alignItems:'center'}}>
                <img src={perfilImg} alt="perfil" style={{width:80,height:80,borderRadius:'50%',border:'3px solid #e0f5e0'}} />
                <div>
                  <h2 style={{margin:0,fontSize:24,fontWeight:700,color:'#333'}}>
                    {(() => {
                      const nombreCompleto = ((otherParticipant as any)?.apellido ? `${otherParticipant?.name} ${(otherParticipant as any).apellido}` : otherParticipant?.name) || 'Postulante';
                      const partes = nombreCompleto.split(' ');
                      if (partes.length === 1) return partes[0].charAt(0).toUpperCase() + partes[0].slice(1).toLowerCase();
                      const nombre = partes[0].charAt(0).toUpperCase() + partes[0].slice(1).toLowerCase();
                      const apellido = partes[partes.length - 1].charAt(0).toUpperCase();
                      return `${nombre} ${apellido}`;
                    })()}
                  </h2>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginTop:4}}>
                    <span style={{color:'#ffd700',fontSize:18}}>⭐⭐⭐⭐⭐</span>
                    <span 
                      onClick={() => {
                        const resenasEjemplo = [
                          { id:1, nombre:'María González', fecha:'2024-12-15', rating:5, comentario:'Excelente trabajadora, muy responsable y detallista. Siempre puntual y con gran calidad en su trabajo. La recomiendo totalmente.', trabajo:'Limpieza semanal - Palermo' },
                          { id:2, nombre:'Carlos Rodríguez', fecha:'2024-11-28', rating:5, comentario:'Muy profesional y organizada. Dejó mi casa impecable y es muy confiable. Sin duda la volvería a contratar.', trabajo:'Limpieza profunda - Recoleta' },
                          { id:3, nombre:'Ana Martínez', fecha:'2024-10-10', rating:4, comentario:'Buen trabajo en general, muy cuidadosa con las cosas delicadas. Solo llegó un poco tarde una vez pero todo lo demás perfecto.', trabajo:'Planchado y limpieza - Belgrano' },
                          { id:4, nombre:'Roberto Silva', fecha:'2024-09-22', rating:5, comentario:'Increíble trabajadora. Muy honesta, responsable y hace un trabajo excepcional. Mi familia y yo estamos muy contentos.', trabajo:'Servicio completo - Villa Crespo' },
                          { id:5, nombre:'Laura Fernández', fecha:'2024-08-15', rating:5, comentario:'La mejor empleada que hemos tenido. Muy profesional, organizada y siempre con una sonrisa. Altamente recomendada.', trabajo:'Limpieza y organización - Caballito' }
                        ];
                        setResenasSeleccionadas(resenasEjemplo); setShowResenas(true);
                      }}
                      style={{color:'#666',fontSize:16,cursor:'pointer',textDecoration:'underline'}}
                    >4.8 (127 reseñas)</span>
                  </div>
                  <div style={{color:'#888',fontSize:14,marginTop:4}}>
                    Palermo, CABA • Lunes a Viernes, 8:00 - 18:00
                  </div>
                </div>
              </div>
              {/* Sección de experiencia */}
              <div style={{marginBottom:24}}>
                <h3 style={{fontSize:18,fontWeight:600,color:'#333',marginBottom:12,borderBottom:'2px solid #1db954',paddingBottom:4}}>Experiencia</h3>
                <div style={{background:'#f8f9fa',padding:16,borderRadius:8,border:'1px solid #e9ecef'}}>
                  <p style={{margin:0,color:'#666',lineHeight:1.6}}>
                    <strong>5 años de experiencia</strong> en servicios domésticos. Especializada en limpieza profunda, 
                    organización de espacios y cuidado de telas delicadas. Trabajé en hogares de Palermo, 
                    Recoleta y Belgrano con excelentes referencias.
                  </p>
                </div>
              </div>
              {/* Sección de habilidades */}
              <div style={{marginBottom:24}}>
                <h3 style={{fontSize:18,fontWeight:600,color:'#333',marginBottom:12,borderBottom:'2px solid #1db954',paddingBottom:4}}>Habilidades</h3>
                <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                  {['Planchar', 'Cocinar', 'Limpiar', 'Aspirar', 'Lavar a mano', 'Coser'].map(habilidad => (
                    <span key={habilidad} style={{
                      background:'#e0f5e0',
                      color:'#1db954',
                      padding:'6px 12px',
                      borderRadius:16,
                      fontSize:14,
                      fontWeight:500
                    }}>{habilidad}</span>
                  ))}
                </div>
              </div>
              {/* Sección de referencias */}
              <div style={{marginBottom:24}}>
                <h3 style={{fontSize:18,fontWeight:600,color:'#333',marginBottom:12,borderBottom:'2px solid #1db954',paddingBottom:4}}>Referencias</h3>
                <div style={{background:'#f8f9fa',padding:16,borderRadius:8,border:'1px solid #e9ecef'}}>
                  <div style={{marginBottom:12}}>
                    <div style={{fontWeight:600,color:'#333',marginBottom:4}}>María González - Palermo</div>
                    <div style={{color:'#666',fontSize:14,fontStyle:'italic'}}>
                      "Excelente trabajadora, muy responsable y detallista. La recomiendo totalmente."
                    </div>
                  </div>
                  <div>
                    <div style={{fontWeight:600,color:'#333',marginBottom:4}}>Carlos Rodríguez - Recoleta</div>
                    <div style={{color:'#666',fontSize:14,fontStyle:'italic'}}>
                      "Muy profesional, siempre puntual y con gran calidad en su trabajo."
                    </div>
                  </div>
                </div>
              </div>
              {/* Sección de disponibilidad */}
              <div style={{marginBottom:24}}>
                <h3 style={{fontSize:18,fontWeight:600,color:'#333',marginBottom:12,borderBottom:'2px solid #1db954',paddingBottom:4}}>Disponibilidad</h3>
                <div style={{background:'#f8f9fa',padding:16,borderRadius:8,border:'1px solid #e9ecef'}}>
                  <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
                    <div style={{textAlign:'center'}}>
                      <div style={{fontWeight:600,color:'#333'}}>Lunes - Viernes</div>
                      <div style={{color:'#666',fontSize:14}}>8:00 - 18:00</div>
                    </div>
                    <div style={{textAlign:'center'}}>
                      <div style={{fontWeight:600,color:'#333'}}>Sábados</div>
                      <div style={{color:'#666',fontSize:14}}>9:00 - 14:00</div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Botones de acción */}
              <div style={{display:'flex',gap:12,justifyContent:'flex-end'}}>
                <button onClick={()=>setShowProfile(false)} style={{
                  padding:'12px 24px',
                  borderRadius:8,
                  border:'1px solid #ddd',
                  background:'#f5f5f5',
                  color:'#666',
                  fontWeight:600,
                  cursor:'pointer'
                }}>Cerrar</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de reseñas - idéntico */}
        {showResenas && (
          <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setShowResenas(false)}>
            <div style={{background:'#fff',borderRadius:16,padding:32,minWidth:500,maxWidth:700,boxShadow:'0 8px 32px rgba(0,0,0,0.3)',position:'relative',maxHeight:'90vh',overflowY:'auto'}} onClick={e=>e.stopPropagation()}>
              <button onClick={()=>setShowResenas(false)} style={{position:'absolute',top:16,right:16,background:'none',border:'none',fontSize:24,cursor:'pointer',color:'#888'}}>×</button>
              <h2 style={{margin:0,marginBottom:24,fontSize:24,fontWeight:700,color:'#333',textAlign:'center'}}>Reseñas</h2>
              <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:12,marginBottom:24}}>
                <span style={{color:'#ffd700',fontSize:24}}>⭐⭐⭐⭐⭐</span>
                <span style={{fontSize:20,fontWeight:600,color:'#333'}}>4.8</span>
                <span style={{color:'#666',fontSize:16}}>({resenasSeleccionadas.length} reseñas)</span>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:16}}>
                {resenasSeleccionadas.map((resena) => (
                  <div key={resena.id} style={{
                    background:'#f8f9fa',
                    padding:20,
                    borderRadius:12,
                    border:'1px solid #e9ecef'
                  }}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
                      <div>
                        <div style={{fontWeight:600,color:'#333',fontSize:16,marginBottom:4}}>{resena.nombre}</div>
                        <div style={{color:'#666',fontSize:14}}>{resena.trabajo}</div>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <div style={{display:'flex',gap:2}}>
                          {[...Array(5)].map((_, i) => (
                            <span key={i} style={{
                              color: i < resena.rating ? '#ffd700' : '#ddd',
                              fontSize:16
                            }}>⭐</span>
                          ))}
                        </div>
                        <div style={{color:'#888',fontSize:12}}>
                          {new Date(resena.fecha).toLocaleDateString('es-AR')}
                        </div>
                      </div>
                    </div>
                    <p style={{margin:0,color:'#666',lineHeight:1.5,fontSize:14}}>
                      "{resena.comentario}"
                    </p>
                  </div>
                ))}
              </div>
              <div style={{display:'flex',justifyContent:'center',marginTop:24}}>
                <button onClick={()=>setShowResenas(false)} style={{
                  padding:'12px 24px',
                  borderRadius:8,
                  border:'1px solid #ddd',
                  background:'#f5f5f5',
                  color:'#666',
                  fontWeight:600,
                  cursor:'pointer'
                }}>Cerrar</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de alerta para números de teléfono */}
        {showPhoneAlert && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} onClick={() => setShowPhoneAlert(false)}>
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 32,
              minWidth: 400,
              maxWidth: 500,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              position: 'relative',
              textAlign: 'center'
            }} onClick={e => e.stopPropagation()}>
              <div style={{
                fontSize: 48,
                marginBottom: 16,
                color: '#ff6b6b'
              }}>📵</div>
              <h2 style={{
                margin: 0,
                marginBottom: 16,
                fontSize: 24,
                fontWeight: 700,
                color: '#333'
              }}>¡Ups! No se permiten números de teléfono</h2>
              <p style={{
                margin: 0,
                marginBottom: 24,
                color: '#666',
                lineHeight: 1.5,
                fontSize: 16
              }}>
                Por seguridad y privacidad, no puedes compartir números de teléfono en los chats. 
                Si necesitas contactar a alguien, puedes usar la función de contacto oficial de la plataforma.
              </p>
              <button onClick={() => setShowPhoneAlert(false)} style={{
                padding: '12px 24px',
                borderRadius: 8,
                border: 'none',
                background: '#693fea',
                color: '#fff',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: 16
              }}>
                Entendido
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ChatIndividual;